// Behavioral enemy abilities (Phase 5): v1 shipped `abilities` as flavor
// strings — these are the real, telegraphed signature moves, one per type.
//   punk lane_chase        burst toward the player after a head-bob telegraph
//   hipster distracted     stop-start walk + one coffee speed burst
//   business briefcase     hitbox widens during a telegraphed swing
//   jogger mirror_jump     jumps when you jump in range — beat it with slide
//   artist paint_slick     drops a slick that forces a lane change
//   pedestrian umbrella    periodic overhead block — no jump-over during twirl

import type { EnemyRig } from './avatars';
import type { EnemyDef } from '@/content/enemies';

export type BehaviorPhase = 'idle' | 'telegraph' | 'active' | 'done';

export interface BehaviorState {
  phase: BehaviorPhase;
  timer: number;
  used: boolean;
  cycleTimer: number;
}

export function freshBehavior(): BehaviorState {
  return { phase: 'idle', timer: 0, used: false, cycleTimer: 0 };
}

export interface BehaviorEffects {
  /** Multiplier on walk speed this frame */
  speedFactor: number;
  /** Contact hit radius (default 0.6; briefcase swing widens it) */
  hitRadius: number;
  /** Enemy is airborne (mirror jump) — jump-over fails, slide sweeps */
  airborneY: number;
  /** Overhead blocked (umbrella twirl) — jump-over fails */
  blocked: boolean;
  /** Drop a paint slick at the enemy's position this frame */
  dropSlick: boolean;
}

export interface BehaviorCtx {
  z: number; // Enemy world z (negative = ahead of player)
  playerJumping: boolean;
  dt: number;
}

const DEFAULT_EFFECTS: BehaviorEffects = {
  speedFactor: 1,
  hitRadius: 0.6,
  airborneY: 0,
  blocked: false,
  dropSlick: false,
};

export function updateBehavior(
  def: EnemyDef,
  state: BehaviorState,
  rig: EnemyRig,
  ctx: BehaviorCtx,
): BehaviorEffects {
  const fx = { ...DEFAULT_EFFECTS };
  state.timer += ctx.dt;
  state.cycleTimer += ctx.dt;

  switch (def.id) {
    case 'punk': {
      // One aggressive burst once close enough, after a 0.4s head-bob
      if (state.phase === 'idle' && !state.used && ctx.z > -22) {
        state.phase = 'telegraph';
        state.timer = 0;
      } else if (state.phase === 'telegraph') {
        rig.head.rotation.x = Math.sin(state.timer * 30) * 0.25; // Head bob
        if (state.timer >= 0.4) {
          state.phase = 'active';
          state.timer = 0;
        }
      } else if (state.phase === 'active') {
        fx.speedFactor = 2.2;
        if (state.timer >= 0.8) {
          state.phase = 'done';
          state.used = true;
          rig.head.rotation.x = 0;
        }
      }
      break;
    }
    case 'hipster': {
      // Stop-start walk rhythm (1.2s walk / 0.4s pause)
      const cycle = state.cycleTimer % 1.6;
      if (cycle > 1.2) fx.speedFactor = 0;
      // One coffee burst when close
      if (!state.used && ctx.z > -14) {
        state.used = true;
        state.phase = 'active';
        state.timer = 0;
      }
      if (state.phase === 'active') {
        fx.speedFactor = 1.8;
        if (state.timer >= 0.8) state.phase = 'done';
      }
      break;
    }
    case 'business': {
      // Cycle: 1.4s idle → 0.5s telegraph (arm raise) → 0.6s wide swing
      const cycle = state.cycleTimer % 2.5;
      if (cycle >= 1.4 && cycle < 1.9) {
        rig.rightArm.rotation.x = -1.6; // Wind-up telegraph
      } else if (cycle >= 1.9) {
        rig.rightArm.rotation.x = 0.9; // Swing
        fx.hitRadius = 1.0;
      }
      break;
    }
    case 'jogger': {
      // Mirrors the player's jump once when in range — slide sweeps instead
      if (!state.used && ctx.playerJumping && ctx.z > -9 && ctx.z < -1) {
        state.used = true;
        state.phase = 'active';
        state.timer = 0;
      }
      if (state.phase === 'active') {
        const progress = state.timer / 0.7;
        if (progress >= 1) {
          state.phase = 'done';
        } else {
          fx.airborneY = Math.sin(progress * Math.PI) * def.jumpHeight;
        }
      }
      break;
    }
    case 'artist': {
      // Drops one paint slick when close
      if (!state.used && ctx.z > -13) {
        state.used = true;
        fx.dropSlick = true;
      }
      break;
    }
    case 'pedestrian': {
      // Umbrella cycle: 1.2s open (safe) → 0.8s twirl (overhead blocked)
      const cycle = state.cycleTimer % 2.0;
      if (cycle >= 1.2) {
        fx.blocked = true;
        rig.rightArm.rotation.z = (cycle - 1.2) * 12; // Twirl
      } else {
        rig.rightArm.rotation.z = 0;
      }
      break;
    }
    case 'sensei':
      break; // Sensei has no field ability; the encounter is the fight
  }

  return fx;
}
